import i18nFactory from '@yandex-int/i18n';
import { Button } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { formatNumber } from '../../../service/FormatHelpers/FormatHelpers';
import { pluralizeWord } from '../../../service/Pluralize/Pluralize';
import { RecommendationCharacter, RecommendationType } from '../../../typings/recommendations';
import { Image2 } from '../../Image2/Image2';
import { Shape2Icon } from '../../Shape2/Shape2';
import { RecommendationCard, RecommendationCardView } from '../RecommendationCard';

import './AddBannerImageRecommendationCard.css';
import * as keyset from './AddBannerImageRecommendationCard.i18n';
import * as bannerWithImage1x from './BannerWithImage@1x.jpg';
import * as bannerWithImage2x from './BannerWithImage@2x.jpg';
import * as bannerWithImage3x from './BannerWithImage@3x.jpg';
import * as bannerWithoutImage1x from './BannerWithoutImage@1x.jpg';
import * as bannerWithoutImage2x from './BannerWithoutImage@2x.jpg';
import * as bannerWithoutImage3x from './BannerWithoutImage@3x.jpg';

const i18n = i18nFactory(keyset);

interface RecommendationsPopupViewProps {
    character: RecommendationCharacter;
    icon: Shape2Icon;
    view: RecommendationCardView.recommendationsPopup;
    onShow?: (view: RecommendationCardView, type: RecommendationType) => void;
}

interface NormalViewProps {
    character: RecommendationCharacter;
    icon: Shape2Icon;
    view: RecommendationCardView.normal;
    bannersNumber: number;
    bannersPageUrl: string;
    onShow?: (view: RecommendationCardView, type: RecommendationType) => void;
}

export type AddBannerImageRecommendationCardProps = RecommendationsPopupViewProps | NormalViewProps;

export class AddBannerImageRecommendationCard extends React.Component<AddBannerImageRecommendationCardProps> {
    render() {
        const { view, character, icon, onShow } = this.props;

        return (
            <RecommendationCard
                type={RecommendationType.addImageToBanner}
                icon={icon}
                character={character}
                title={this.getTitle()}
                titleHint={this.getTitleHint()}
                view={view}
                isInProgress={false}
                buttons={this.getButtons()}
                onShow={onShow}
            >
                {this.getBody()}
            </RecommendationCard>
        );
    }

    private getTitleHint = (): string => {
        if (this.props.view === RecommendationCardView.recommendationsPopup) {
            return i18n('popup-view-title-hint');
        }

        return i18n('normal-view-title-hint');
    };

    private getTitle = (): string => {
        if (this.props.view === RecommendationCardView.recommendationsPopup) {
            return i18n('popup-view-title');
        }

        return i18n('normal-view-title');
    };

    private getBody = () => {
        return (
            <>
                <p className={Bem.cls('add-banner-image-recommendation-card', 'body-text')}>
                    {i18n('banner-image-desc1')}
                </p>

                <div
                    className={Bem.cls({
                        block: 'add-banner-image-recommendation-card',
                        elem: 'example-wrapper',
                        mods: { 'without-image': true }
                    })}
                >
                    <div className={Bem.cls('add-banner-image-recommendation-card', 'example-title')}>
                        {i18n('without-image')}
                    </div>

                    <Image2
                        src={{
                            src: bannerWithoutImage1x,
                            src2x: bannerWithoutImage2x,
                            src3x: bannerWithoutImage3x
                        }}
                        cls={Bem.cls('add-banner-image-recommendation-card', 'example')}
                    />
                </div>

                <div
                    className={Bem.cls({
                        block: 'add-banner-image-recommendation-card',
                        elem: 'example-wrapper',
                        mods: { 'with-image': true }
                    })}
                >
                    <div className={Bem.cls('add-banner-image-recommendation-card', 'example-title')}>
                        {i18n('with-image')}
                    </div>

                    <Image2
                        src={{
                            src: bannerWithImage1x,
                            src2x: bannerWithImage2x,
                            src3x: bannerWithImage3x
                        }}
                        cls={Bem.cls('add-banner-image-recommendation-card', 'example')}
                    />
                </div>

                <p className={Bem.cls('add-banner-image-recommendation-card', 'body-text')}>
                    {i18n('banner-image-desc2')}
                </p>
            </>
        );
    };

    private getButtons = () => {
        if (this.props.view === RecommendationCardView.recommendationsPopup) {
            return undefined;
        }

        const { bannersNumber, bannersPageUrl } = this.props;
        const formattedItemsLength = formatNumber(bannersNumber);

        const buttonText = pluralizeWord(
            [
                i18n('show-one-banner-button', {
                    count: formattedItemsLength
                }),
                i18n('show-some-banner-button', {
                    count: formattedItemsLength
                }),
                i18n('show-many-banner-button', {
                    count: formattedItemsLength
                })
            ],
            bannersNumber
        );

        return [<Button key={1} theme={'action'} size={'n'} text={buttonText} type={'link'} url={bannersPageUrl} />];
    };
}
